import { linkFromUrl, type Link } from "@tiptap-content/extensions/link/link-attrs"
import {
  isLinkNode,
  LinkInlineContentExtension,
} from "@tiptap-content/extensions/link/link-content-extensions"
import { InputRule, nodePasteRule } from "@tiptap/core"
import { closeHistory } from "@tiptap/pm/history"
import { NodeSelection, Plugin } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { find } from "linkifyjs"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asAttachmentId } from "../../analytics/types"
import { LinkComponent } from "./link-component"

/**
 * Which opener started the link dialog. Each is an EditorSurface in its own right, so the operations
 * a submit reports name the gesture the user made rather than the dialog they all share.
 */
export type LinkDialogOrigin = "floating-toolbar" | "keyboard" | "link-popover"

export interface LinkInlineOptions {
  openLinkDialog: ((origin: LinkDialogOrigin) => void) | null
}

export const ExtensionLinkInline = LinkInlineContentExtension.extend<LinkInlineOptions>({
  addOptions() {
    return {
      openLinkDialog: null,
    }
  },

  addKeyboardShortcuts() {
    const type = this.type
    return {
      // Enter right after a typed URL converts it like space does (§6). Input rules
      // don't fire on Enter, so this converts the word before the cursor itself, then
      // returns false so Enter still splits the paragraph as usual.
      Enter: ({ editor }) => {
        const { selection } = editor.state
        const $from = selection.$from
        if (!selection.empty || !$from.parent.isTextblock) return false
        const textBefore = $from.parent.textBetween(0, $from.parentOffset)
        const candidate = /(\S+)$/.exec(textBefore)?.[1]
        if (!candidate) return false
        const [link] = find(candidate, { defaultProtocol: "https" })
        if (!link?.isLink || link.value !== candidate) return false

        const start = $from.pos - candidate.length
        const attrs = linkFromUrl(link.href, candidate)
        editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_AUTOLINK, "keyboard", {
            attachmentId: asAttachmentId(attrs.attachmentId),
            url: attrs.url,
          })
          .command(({ tr }) => {
            closeHistory(tr).replaceWith(start, $from.pos, type.create(attrs))
            return true
          })
          .run()
        return false
      },

      // Mod-k opens the link dialog: over selected text it adds a token; over a
      // selected token (any form) it edits it.
      "Mod-k": ({ editor }) => {
        const { selection } = editor.state
        const onToken = selection instanceof NodeSelection && isLinkNode(selection.node)
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to)
        if (!onToken && selectedText === "") return false
        this.options.openLinkDialog?.("keyboard")
        return true
      },
    }
  },

  addInputRules() {
    const type = this.type
    return [
      // Typing a URL + space replaces the matched run with a hyperlink token (§6).
      // The broad word-plus-space match fires on every word; linkify decides whether
      // it's a URL (scheme-less domains like nytimes.com included) — returning null
      // cancels the rule, so the space inserts normally.
      new InputRule({
        find: /(?:^|\s)(\S+) $/,
        handler({ state, range, match, chain }) {
          const candidate = match[1]
          const [link] = find(candidate, { defaultProtocol: "https" })
          if (!link?.isLink || link.value !== candidate) return null
          const start = range.to - candidate.length
          const attrs = linkFromUrl(link.href, candidate)
          // Its own history event, so ⌘Z reverts just the conversion — back to the
          // typed URL text — instead of the whole typing group (§6's escape hatch).
          closeHistory(state.tr).replaceWith(start, range.to, [
            type.create(attrs),
            state.schema.text(" "),
          ])
          // The rule's `chain` is bound to the very transaction above, so this labels it.
          chain()
            .label(OPERATION_CONTRACTS.OP_AUTOLINK, "markdown", {
              attachmentId: asAttachmentId(attrs.attachmentId),
              url: attrs.url,
            })
            .run()
        },
      }),
    ]
  },

  addProseMirrorPlugins() {
    const type = this.type
    return [
      new Plugin({
        props: {
          // Pasting a URL over selected text keeps the text: the selection becomes a
          // hyperlink token *titled with the selected text* (§6) — the token-world
          // version of the old mark's linkOnPaste. Bare-cursor URL pastes fall through
          // to the paste rule below.
          handlePaste(view, event) {
            const { selection, doc, tr } = view.state
            if (selection.empty || !view.editable) return false
            const text = event.clipboardData?.getData("text/plain").trim()
            if (!text) return false
            const [link] = find(text, { defaultProtocol: "https" })
            if (!link?.isLink || link.value !== text) return false

            // Pasting over a selected token re-links it: same attachment identity and
            // label, new destination — like editing its URL in the dialog.
            const selectedToken =
              selection instanceof NodeSelection && isLinkNode(selection.node)
                ? (selection.node.attrs as Link)
                : null
            const attrs = selectedToken
              ? {
                  ...linkFromUrl(link.href, selectedToken.title),
                  attachmentId: selectedToken.attachmentId,
                }
              : linkFromUrl(link.href, doc.textBetween(selection.from, selection.to, " "))
            view.dispatch(closeHistory(tr).replaceSelectionWith(type.create(attrs)))
            return true
          },
        },
      }),
    ]
  },

  addPasteRules() {
    return [
      // A URL on the clipboard (bare, or inside pasted text) inserts a hyperlink
      // token titled with the URL as written (§6).
      nodePasteRule({
        find: (text) =>
          find(text, { defaultProtocol: "https" })
            .filter((link) => link.isLink)
            .map((link) => ({ index: link.start, text: link.value, data: link })),
        type: this.type,
        getAttributes: (match) => {
          const link = match.data as { href: string; value: string }
          return linkFromUrl(link.href, link.value)
        },
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkComponent)
  },
})
